/**
 * کارت‌های بانکی کاربر
 * =====================
 * پیش از این، ثبت کارت فقط گوشه‌ای از صفحه کیف پول بود — صفحه‌ای سنگین که
 * قیمت، موجودی و نمودار هم می‌گیرد. اگر هر کدام از آن‌ها به مشکل می‌خورد،
 * کاربر به کارت‌هایش هم دسترسی نداشت. اینجا بخشی مستقل و سبک است که فقط یک
 * درخواست می‌زند و هیچ وابستگی‌ای به داده بازار ندارد.
 *
 * نکته‌ای که تجربه ثبت کارت را می‌سازد: کاربر باید **پیش از** فشردن دکمه
 * بفهمد شماره‌اش ایراد دارد (Luhn و تشخیص بانک همان لحظه)، و **پس از** رد
 * شدن بفهمد چرا رد شد و حالا چه کند. هر دو اینجا هست.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CreditCard, Plus, Trash2, ShieldCheck, AlertTriangle, Info, Check, Clock, X,
} from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Button, Modal, Rows, Empty, Field } from '../components/UI'
import { useAuth, toast } from '../store'
import { get, post, del } from '../lib/api'
import { clsx, fa, cardNumber, faDate, toEnDigits, asArray } from '../lib/format'

type BankCard = {
  id: number; cardNumber: string; masked: string; iban: string; bankName: string
  status: string; statusLabel: string; statusHint: string; createdAt: string
}

/**
 * الگوریتم Luhn — همان بررسی‌ای که خود بانک روی شماره کارت انجام می‌دهد.
 * اینجا تکرار می‌شود تا غلط تایپی پیش از خرج‌شدن یک استعلام پولی گرفته شود.
 */
export function luhnOk(card: string): boolean {
  if (card.length !== 16 || !/^\d+$/.test(card)) return false
  let total = 0
  for (let i = 0; i < 16; i++) {
    let d = Number(card[i])
    if (i % 2 === 0) { d *= 2; if (d > 9) d -= 9 }
    total += d
  }
  return total % 10 === 0
}

export const BANK_HINTS: Record<string, string> = {
  '603799': 'بانک ملی ایران', '589210': 'بانک سپه', '627353': 'بانک تجارت',
  '603770': 'بانک کشاورزی', '628023': 'بانک مسکن', '627412': 'بانک اقتصاد نوین',
  '622106': 'بانک پارسیان', '502229': 'بانک پاسارگاد', '603769': 'بانک صادرات ایران',
  '610433': 'بانک ملت', '589463': 'بانک رفاه کارگران', '621986': 'بانک سامان',
  '636214': 'بانک آینده', '639346': 'بانک سینا', '504706': 'بانک شهر',
  '502806': 'بانک شهر', '627760': 'پست بانک ایران', '639217': 'بانک کشاورزی',
  '639347': 'بانک پاسارگاد', '627381': 'بانک انصار', '505785': 'بانک ایران زمین',
  '636795': 'بانک مرکزی', '639599': 'بانک قوامین', '627488': 'بانک کارآفرین',
  '502908': 'بانک توسعه تعاون', '639607': 'بانک سرمایه', '636949': 'بانک حکمت ایرانیان',
  '505416': 'بانک گردشگری', '606373': 'بانک قرض‌الحسنه مهر ایران',
  '628157': 'مؤسسه اعتباری توسعه', '507677': 'مؤسسه نور',
}

const TONE: Record<string, string> = {
  approved: 'border-up/30 bg-up/[.06] text-up',
  pending: 'border-gold/30 bg-gold/[.06] text-gold',
  rejected: 'border-down/30 bg-down/[.06] text-down',
}

function StatusChip({ status, label }: { status: string; label: string }) {
  const Icon = status === 'approved' ? Check : status === 'rejected' ? X : Clock
  return (
    <span className={clsx('inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium',
      TONE[status] ?? 'border-white/10 bg-white/[.05] text-slate-400')}>
      <Icon className="h-3 w-3" />{label || status}
    </span>
  )
}

export default function CardsPage() {
  const { user } = useAuth()
  const [info, setInfo] = useState<any>(null)
  const [rows, setRows] = useState<BankCard[] | null>(null)
  const [open, setOpen] = useState(false)

  const load = () => {
    get('/wallet/cards/overview')
      .then((r: any) => { setInfo(r); setRows(asArray<BankCard>(r, 'data')) })
      // اگر این درخواست به هر دلیلی نگرفت، صفحه نباید خالی بماند: فهرست
      // خالی نشان داده می‌شود و دکمه افزودن همچنان کار می‌کند.
      .catch(() => { setRows([]); setInfo(null) })
  }
  useEffect(() => { load() }, [])

  const list = rows ?? []
  const approved = list.filter((c) => c.status === 'approved').length
  const max = info?.max ?? 0
  const atCap = max > 0 && list.length >= max
  const noName = !(info?.fullName ?? user?.fullName ?? '').trim()

  return (
    <PanelShell title="کارت‌های بانکی" subtitle="کارت‌هایی که برداشت ریالی شما به آن‌ها واریز می‌شود">
      {/* راهنمای کوتاه — پیش از فهرست، چون بیشترِ سؤال‌ها همین‌هاست */}
      <Card className="mb-5">
        <div className="flex flex-wrap items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-brand-500/30 bg-brand-500/10 text-brand-300">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div className="flex-1 text-xs leading-7 text-slate-400">
            کارت باید <b className="text-slate-200">به نام خودتان</b> باشد. مالکیت کارت به‌صورت
            خودکار از بانک استعلام می‌شود و اگر نام صاحب کارت با نام حساب شما یکی باشد،
            بلافاصله تأیید می‌گردد. برداشت ریالی فقط به کارت‌های تأییدشده انجام می‌شود.
            {max > 0 && <> حداکثر {fa(max)} کارت می‌توانید ثبت کنید.</>}
          </div>
        </div>
      </Card>

      {noName && (
        <Card className="mb-5 border-gold/25 bg-gold/[.04]">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <AlertTriangle className="h-5 w-5 shrink-0 text-gold" />
            <div className="flex-1">
              <div className="font-bold text-white">هنوز نام و نام خانوادگی ثبت نکرده‌اید</div>
              <div className="mt-1 text-xs text-slate-400">
                بدون آن، نام صاحب کارت با چیزی مقایسه نمی‌شود و کارت شما تأیید خودکار نخواهد شد.
              </div>
            </div>
            <Link to="/kyc" className="btn-ghost !py-2 text-xs">تکمیل اطلاعات هویتی</Link>
          </div>
        </Card>
      )}

      <Card className="!p-0">
        <div className="flex flex-wrap items-center gap-3 border-b border-white/[.06] p-4">
          <h3 className="font-bold text-white">کارت‌های من</h3>
          {rows && (
            <span className="chip text-[11px] text-slate-400">
              {fa(list.length)}{max > 0 && <> از {fa(max)}</>} ثبت‌شده
              {approved > 0 && <> — {fa(approved)} تأییدشده</>}
            </span>
          )}
          <Button variant="ghost" className="mr-auto !py-2 text-xs" disabled={atCap}
            onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> افزودن کارت
          </Button>
        </div>

        {!rows ? <div className="p-4"><Rows n={3} h="h-24" /></div>
          : list.length === 0 ? (
            <Empty text="هنوز کارت بانکی ثبت نکرده‌اید" icon={<CreditCard className="h-6 w-6" />} />
          ) : (
            <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((c) => (
                <CardTile key={c.id} c={c} onDone={load} />
              ))}
            </div>
          )}

        {atCap && (
          <div className="flex items-center gap-2 border-t border-white/[.06] p-4 text-[11px] text-slate-500">
            <Info className="h-3.5 w-3.5" />
            به سقف تعداد کارت رسیده‌اید؛ برای ثبت کارت تازه، یکی از کارت‌های قبلی را حذف کنید.
          </div>
        )}
      </Card>

      <AddCardModal open={open} fullName={info?.fullName ?? ''}
        onClose={() => setOpen(false)} onDone={load} />
    </PanelShell>
  )
}

function CardTile({ c, onDone }: { c: BankCard; onDone: () => void }) {
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState(false)

  const remove = async () => {
    setBusy(true)
    try { await del(`/wallet/cards/${c.id}`); toast.ok('کارت حذف شد'); onDone() }
    catch (e: any) { toast.err(e.message) } finally { setBusy(false); setConfirm(false) }
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-brand-600/25 via-white/[.03] to-aqua/15 p-4">
      <div className="mb-6 flex items-center justify-between gap-2">
        <span className="text-xs text-slate-300">{c.bankName || 'نامشخص'}</span>
        <StatusChip status={c.status} label={c.statusLabel} />
      </div>

      {/* شماره پوشانده نمایش داده می‌شود؛ صفحه ممکن است جلوی چشم دیگری باز باشد */}
      <div className="num mb-2 text-lg tracking-widest text-white" dir="ltr">
        {cardNumber(c.masked || c.cardNumber)}
      </div>

      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="num text-[11px] text-slate-400" dir="ltr">{c.iban || '—'}</span>
        <span className="text-[10px] text-slate-500">{faDate(c.createdAt, false)}</span>
      </div>

      {c.statusHint && (
        <p className="mb-3 text-[11px] leading-5 text-slate-400">{c.statusHint}</p>
      )}

      {confirm ? (
        <div className="flex gap-2">
          <button onClick={remove} disabled={busy}
            className="flex-1 rounded-lg border border-down/40 bg-down/10 py-1.5 text-[11px] text-down transition hover:bg-down/20">
            {busy ? '…' : 'حذف شود'}
          </button>
          <button onClick={() => setConfirm(false)}
            className="flex-1 rounded-lg border border-white/10 py-1.5 text-[11px] text-slate-400 transition hover:bg-white/[.05]">
            انصراف
          </button>
        </div>
      ) : (
        <button onClick={() => setConfirm(true)}
          className="flex items-center gap-1.5 text-[11px] text-slate-500 transition hover:text-down">
          <Trash2 className="h-3.5 w-3.5" /> حذف کارت
        </button>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════ پنجره افزودن کارت */
export function AddCardModal({
  open, fullName, onClose, onDone,
}: { open: boolean; fullName?: string; onClose: () => void; onDone: () => void }) {
  const [card, setCard] = useState('')
  const [iban, setIban] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<any>(null)

  const detected = BANK_HINTS[card.slice(0, 6)] || ''
  const tooShort = card.length > 0 && card.length < 16
  const badLuhn = card.length === 16 && !luhnOk(card)
  const ready = card.length === 16 && !badLuhn && !loading

  const close = () => { setCard(''); setIban(''); setResult(null); onClose() }

  const submit = async () => {
    setLoading(true)
    setResult(null)
    try {
      const r: any = await post('/wallet/cards', {
        card_number: toEnDigits(card), iban: toEnDigits(iban), bank_name: detected,
      })
      setResult(r)
      if (r?.status === 'approved') toast.ok(r.message || 'کارت تأیید شد')
      else if (r?.status === 'rejected') toast.err(r.message || 'کارت تأیید نشد')
      else toast.ok(r?.message || 'کارت ثبت شد')
      onDone()
      // کارت تأییدشده پنجره را می‌بندد؛ در بقیه حالت‌ها کاربر باید پیام را
      // بخواند و بفهمد چرا تأیید نشده است.
      if (r?.status === 'approved') setTimeout(close, 1200)
    } catch (e: any) {
      toast.err(e.message)
    } finally { setLoading(false) }
  }

  return (
    <Modal open={open} onClose={close} title="افزودن کارت بانکی">
      <Field
        label="شماره کارت (۱۶ رقم)"
        error={badLuhn ? 'شماره کارت معتبر نیست؛ دوباره بررسی کنید' : undefined}
        hint={!badLuhn && detected ? detected
          : tooShort ? `${fa(16 - card.length)} رقم دیگر`
          : 'شماره روی کارت، بدون فاصله'}
      >
        <input className="input num tracking-widest" dir="ltr" inputMode="numeric" maxLength={16}
          value={card} autoFocus
          onChange={(e) => setCard(toEnDigits(e.target.value).replace(/\D/g, '').slice(0, 16))}
          placeholder="6037991234567890" />
      </Field>

      <Field label="شماره شبا (اختیاری)"
             hint="خالی بگذارید تا هنگام استعلام از بانک گرفته شود">
        <input className="input num" dir="ltr" value={iban}
          onChange={(e) => setIban(toEnDigits(e.target.value).toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          maxLength={26} placeholder="IR..." />
      </Field>

      {result && (
        <div className={clsx('mb-4 rounded-xl border p-3 text-xs leading-6',
          result.status === 'approved' ? 'border-up/30 bg-up/[.07]'
            : result.status === 'rejected' ? 'border-down/30 bg-down/[.07]'
            : 'border-gold/30 bg-gold/[.07]')}>
          <div className={clsx('font-semibold',
            result.status === 'approved' ? 'text-up'
              : result.status === 'rejected' ? 'text-down' : 'text-gold')}>
            {result.message}
          </div>
          {result.bankName && <div className="mt-1 text-slate-300">بانک: {result.bankName}</div>}
          {result.iban && <div className="num text-[11px] text-slate-400" dir="ltr">{result.iban}</div>}
          {result.status === 'rejected' && (
            <div className="mt-2 leading-6 text-slate-400">
              اگر مطمئنید کارت به نام خودتان است، بررسی کنید نام ثبت‌شده در حساب
              {fullName ? <> («{fullName}») </> : ' '}
              دقیقاً با نام صاحب کارت یکی باشد؛ در غیر این صورت با پشتیبانی تماس بگیرید.
            </div>
          )}
        </div>
      )}

      <p className="mb-4 text-[11px] leading-6 text-slate-500">
        کارت باید به نام خودتان باشد. مالکیت کارت به‌صورت خودکار از بانک استعلام
        می‌شود و در صورت تطابق نام، بلافاصله تأیید می‌گردد.
      </p>

      <Button loading={loading} onClick={submit} disabled={!ready} className="w-full !py-3">
        ثبت و استعلام کارت
      </Button>
    </Modal>
  )
}
